/** The Empress (docs/spec/03 §5): every sixth Supernova stage. */

import { launchSwarmer } from './dive';
import { atan2, cos, sin } from './math';
import { spawnCapsule } from './powerups';
import { addScore } from './score';
import type { Difficulty } from './tuning';
import { FIELD, NOVA } from './tuning';
import type { Empress, EmpressAttack, GameState } from './types';

const ROTATION: readonly EmpressAttack[] = ['fan', 'swarm', 'lance'];
const START_Y = -40;

export function startEmpress(s: GameState): void {
  s.empresses++;
  const rank = s.empresses;
  const maxHp = NOVA.empressHp + NOVA.empressHpPerRank * rank;
  s.empress = {
    x: FIELD.w / 2,
    y: START_Y,
    hp: maxHp,
    maxHp,
    rank,
    state: 'entering',
    phase: 1,
    t: 0,
    drift: 0,
    attack: 'idle',
    attackT: 0,
    turn: 0,
    lanceX: 0,
    lance: 'none',
    flash: 0,
  };
}

function idle(em: Empress): void {
  em.attack = 'idle';
  em.attackT = 0;
}

/** Bombs spread in a fan centred on the fighter. */
function fan(s: GameState, em: Empress, d: Difficulty): void {
  const n = NOVA.fanBombs[em.phase - 1] ?? 5;
  const centre = atan2(s.player.x - em.x, FIELD.fighterY - em.y);
  for (let i = 0; i < n; i++) {
    const a = centre + (i / (n - 1) - 0.5) * NOVA.fanSpread;
    s.bombs.push({ x: em.x, y: em.y + 14, vx: sin(a) * d.bombSpeed, vy: cos(a) * d.bombSpeed });
  }
  s.events.push({ type: 'bomb', x: em.x, y: em.y + 14 });
}

function attack(s: GameState, em: Empress, d: Difficulty): void {
  const i = em.phase - 1;
  const t = em.attackT++;
  switch (em.attack) {
    case 'idle':
      // Like the hive, she starts nothing at a fighter that is down or still on the pad.
      if (s.player.status !== 'flying') {
        em.attackT = t;
        return;
      }
      if (t >= (NOVA.attackGap[i] ?? 90)) {
        em.attack = ROTATION[em.turn % ROTATION.length] ?? 'fan';
        em.turn++;
        em.attackT = 0;
      }
      return;
    case 'fan':
      if (t % NOVA.fanGap === 0 && t / NOVA.fanGap < NOVA.fanVolleys) fan(s, em, d);
      if (t >= NOVA.fanGap * NOVA.fanVolleys) idle(em);
      return;
    case 'swarm': {
      const count = NOVA.swarm[i] ?? 4;
      if (t % NOVA.swarmGap === 0 && t / NOVA.swarmGap < count) {
        const side = (t / NOVA.swarmGap) % 2 === 0 ? -1 : 1;
        launchSwarmer(s, em.x + side * 26, em.y + 8, d);
      }
      if (t >= NOVA.swarmGap * count) idle(em);
      return;
    }
    case 'lance':
      if (t === 0) {
        em.lance = 'warn';
        em.lanceX = em.x;
        s.events.push({ type: 'lance', phase: 'warn', x: em.lanceX });
      } else if (t === NOVA.lanceWarn) {
        em.lance = 'fire';
        s.events.push({ type: 'lance', phase: 'fire', x: em.lanceX });
      } else if (t >= NOVA.lanceWarn + NOVA.lanceFire) {
        em.lance = 'none';
        s.events.push({ type: 'lance', phase: 'end', x: em.lanceX });
        idle(em);
      }
  }
}

export function updateEmpress(s: GameState, d: Difficulty): void {
  const em = s.empress;
  if (!em) return;
  em.t++;
  if (em.flash > 0) em.flash--;
  if (em.state === 'entering') {
    em.y = START_Y + ((NOVA.empressY - START_Y) * em.t) / NOVA.empressEnter;
    if (em.t >= NOVA.empressEnter) {
      em.state = 'fighting';
      em.t = 0;
    }
    return;
  }
  if (em.state === 'dying') {
    if (em.t % 10 === 1) {
      const x = em.x + s.rng.int(57) - 28;
      const y = em.y + s.rng.int(33) - 16;
      s.events.push({ type: 'empressBlast', x, y });
    }
    em.y += 0.12;
    if (em.t >= NOVA.empressDying) s.empress = null;
    return;
  }
  // She holds still while the lance is out.
  if (em.lance === 'none') em.drift += em.phase === 2 ? 1.4 : 1;
  em.x = FIELD.w / 2 + NOVA.empressSwing * sin(em.drift / 50);
  em.y = NOVA.empressY + 6 * sin(em.drift / 20);
  attack(s, em, d);
}

/** Hurt her (a shot, a Nova). Nothing happens unless she is in the fight. */
export function hitEmpress(s: GameState, damage: number): void {
  const em = s.empress;
  if (!em || em.state !== 'fighting') return;
  em.hp -= damage;
  em.flash = 4;
  s.events.push({ type: 'empressHit', x: em.x, y: em.y });
  if (em.hp > 0) {
    if (em.phase === 1 && em.hp * 2 <= em.maxHp) {
      em.phase = 2;
      s.events.push({ type: 'empressPhase' });
    }
    return;
  }
  em.hp = 0;
  em.state = 'dying';
  em.t = 0;
  if (em.lance !== 'none') s.events.push({ type: 'lance', phase: 'end', x: em.lanceX });
  em.lance = 'none';
  em.attack = 'idle';
  s.bombs = [];
  const points = NOVA.empressPoints * em.rank;
  addScore(s, points);
  s.events.push({ type: 'empressKilled', x: em.x, y: em.y, points });
  spawnCapsule(s, em.x - 20, em.y);
  spawnCapsule(s, em.x + 20, em.y);
}
